import { Select, TextLink, Typography } from '@neo4j-ndl/react';
import type { Instance, Project, TierConfig } from '@nx/state';
import { CLOUD_PROVIDER } from '@nx/state';
import type { Validation } from '@nx/stdlib';
import { Objects, isNotNullish } from '@nx/stdlib';
import { useDarkMode } from '@nx/ui';
import { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';

import AwsDark from '../../assets/aws-dark.svg';
import Aws from '../../assets/aws.svg';
import Azure from '../../assets/azure.svg';
import Gcp from '../../assets/gcp_g.svg';
import { friendlyCloudProviderName } from '../entities/helpers';
import type { InstanceFormData } from '../entities/model';
import { BaseInstanceCreateSection } from './base-section';

const providerImgs = (cloudProvider: CLOUD_PROVIDER, isDarkMode: boolean) => {
  if (cloudProvider === CLOUD_PROVIDER.GCP) {
    return Gcp;
  }
  if (cloudProvider === CLOUD_PROVIDER.AZURE) {
    return Azure;
  }

  if (isDarkMode) {
    return AwsDark;
  }
  return Aws;
};

export const formatCspLabel = (
  { label, icon }: { label: string | null; value: CLOUD_PROVIDER; key: CLOUD_PROVIDER; icon: string },
  { context }: { context: string },
) => {
  if (context === 'value') {
    return (
      <div className="flex flex-row items-center gap-2 whitespace-break-spaces">
        <img src={icon} width="20" height="20" title="" className="shrink-0" />
        {label}
      </div>
    );
  } else if (context === 'menu') {
    return (
      <div className="flex flex-row items-center gap-2">
        <img src={icon} width="20" height="20" title="" className="shrink-0" />
        <span>{label}</span>
      </div>
    );
  }
  return null;
};

export const formatRegionLabel = (
  { label, country }: { label: string | null; value: string; key: string; country: string },
  { context }: { context: string },
) => {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
  if (context === 'value') {
    return (
      <div className="flex flex-row items-center gap-2 whitespace-break-spaces">
        <ReactCountryFlag
          countryCode={country}
          title={isNotNullish(country) ? regionNames.of(country) : 'Unknown country flag'}
          className="size-5 shrink-0 self-center"
          style={{ width: '20px', height: '20px' }}
          svg
          aria-label={isNotNullish(country) ? `Flag for ${regionNames.of(country)}` : 'Flag for unknown country'}
        />
        {label}
      </div>
    );
  } else if (context === 'menu') {
    return (
      <div className="flex flex-row items-center gap-2">
        <ReactCountryFlag
          countryCode={country}
          title={isNotNullish(country) ? regionNames.of(country) : 'Unknown country flag'}
          className="shrink-0 self-center"
          style={{ width: '20px', height: '20px' }}
          svg
          aria-label={isNotNullish(country) ? `Flag for ${regionNames.of(country)}` : 'Flag for unknown country'}
        />
        {/* type defaults to 'submit' -- form submits when pressed  (as of @neo4j-ndl/react@3.0.24) */}
        <span>{label}</span>
      </div>
    );
  }
  return null;
};

type Props = {
  data: InstanceFormData;
  onDataChange: (data: Partial<InstanceFormData>) => void;
  tierConfig: TierConfig;
  validation: Validation<InstanceFormData> | null;
  project: Project;
  instance?: Instance;
};

export const CspSection = ({ tierConfig, data, onDataChange, instance }: Props) => {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });

  const isDarkMode = useDarkMode();
  const cloudProviderOptions = Objects.keys(tierConfig.cloudProviderRegions).map((csp) => ({
    label: friendlyCloudProviderName(csp),
    value: csp,
    key: csp,
    icon: providerImgs(csp, isDarkMode),
  }));

  const selectedCsp = useMemo(() => {
    return cloudProviderOptions.find((csp) => csp.value === data.cloudProvider) ?? null;
  }, [cloudProviderOptions, data.cloudProvider]);

  const regions = useMemo(() => {
    return tierConfig.cloudProviderRegions[data.cloudProvider];
  }, [data.cloudProvider, tierConfig.cloudProviderRegions]);

  const regionOptions = useMemo(() => {
    return (
      regions?.map((region) => ({
        label: region.friendly,
        value: region.name,
        key: region.name,
        country: region.country,
      })) ?? []
    );
  }, [regions]);

  const selectedRegion = useMemo(() => {
    return (
      regionOptions.find((region) => region.value === data.region) ??
      regionOptions.find((region) => region.value === instance?.region) ??
      null
    );
  }, [regionOptions, data.region, instance]);

  const handleCspChange = (cloudProvider: CLOUD_PROVIDER | undefined) => {
    if (isNotNullish(cloudProvider)) {
      const [newRegion] = tierConfig.cloudProviderRegions[cloudProvider] ?? [];
      onDataChange({ cloudProvider, region: newRegion?.name });
    }
  };

  const handleRegionChange = (region: string | undefined) => {
    if (isNotNullish(region)) {
      onDataChange({ region });
    }
  };

  return (
    <BaseInstanceCreateSection title="Cloud provider & region">
      <div className="@xs:grid-cols-2 grid grid-cols-1 gap-2">
        {cloudProviderOptions.length > 1 ? (
          <Select
            label="Cloud provider"
            type="select"
            selectProps={{
              value: selectedCsp,
              options: cloudProviderOptions,
              formatOptionLabel: formatCspLabel,
              onChange: (option) => handleCspChange(option?.value),
            }}
            htmlAttributes={{ 'data-testid': 'csp-dropdown' }}
            size="medium"
            isFluid
          />
        ) : (
          <Typography variant="body-medium" className="flex flex-col gap-1">
            <label className="text-neutral-text-weak" htmlFor="cloud-provider">
              Cloud provider
            </label>

            <div className="my-auto flex flex-row items-center gap-2 whitespace-break-spaces p-2" id="cloud-provider">
              <img
                src={providerImgs(data.cloudProvider, isDarkMode)}
                width="20"
                height="20"
                title=""
                className="shrink-0"
                draggable={false}
              />
              {friendlyCloudProviderName(data.cloudProvider)}
            </div>
          </Typography>
        )}
        {regionOptions.length > 1 ? (
          <Select
            label="Region"
            type="select"
            selectProps={{
              value: selectedRegion,
              options: regionOptions,
              onChange: (option) => handleRegionChange(option?.value),
              formatOptionLabel: formatRegionLabel,
            }}
            htmlAttributes={{ 'data-testid': 'region-dropdown' }}
            helpText={
              <>
                Need a different region?{' '}
                <TextLink type="external" href="https://aura.feedback.neo4j.com/auradb-regions">
                  Let us know!
                </TextLink>
              </>
            }
            size="medium"
            isFluid
          />
        ) : (
          <Typography variant="body-medium" className="flex flex-col gap-1">
            <label className="text-neutral-text-weak" htmlFor="region">
              Region
            </label>

            <div className="my-auto flex flex-row items-center gap-2 whitespace-break-spaces p-2" id="region">
              {isNotNullish(selectedRegion) ? (
                <>
                  <ReactCountryFlag
                    countryCode={selectedRegion.country}
                    title={regionNames.of(selectedRegion.country)}
                    className="size-5 shrink-0 self-center"
                    style={{ width: '20px', height: '20px' }}
                    svg
                    aria-label={`Flag for ${regionNames.of(selectedRegion.country)}`}
                    draggable={false}
                  />
                  {selectedRegion.label}
                </>
              ) : (
                'No available region'
              )}
            </div>
          </Typography>
        )}
      </div>
    </BaseInstanceCreateSection>
  );
};
